import React, { useRef } from "react";
import { motion, useScroll } from "framer-motion";
import { PageHero } from "../components/PageHero";
import { CTABand } from "../components/CTABand";
import { Reveal } from "../components/Reveal";
import { WordReveal } from "../components/WordReveal";
import { IMAGES } from "../data/images";
const STEPS = [{
  node: "MCC",
  title: "Learn in Lagos",
  copy: "Students complete the University Pathway Program at MCC in Lekki, with dedicated teachers and advisers."
}, {
  node: "OSSD",
  title: "Earn the Ontario diploma",
  copy: "On completion, students graduate with the Ontario Secondary School Diploma (OSSD) from Rosedale Academy."
}, {
  node: "WORLD",
  title: "Apply globally",
  copy: "The OSSD is accepted by universities in Canada, the USA, the UK and Australia."
}];
const DESTINATIONS = [{
  region: "Canada",
  note: "Home of the OSSD curriculum."
}, {
  region: "United Kingdom",
  note: "OSSD accepted by UK universities."
}, {
  region: "United States",
  note: "OSSD accepted by US universities."
}, {
  region: "Australia",
  note: "OSSD accepted by Australian universities."
}, {
  region: "Singapore & Malaysia",
  note: "IELTS/TOEFL waiver for students scoring above 80% in MCC’s English programme."
}];
const PUBLISHED_OFFERS = ["Massachusetts Institute of Technology", "University of Toronto", "London School of Economics"];
export function Pathways() {
  const pathRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: pathRef,
    offset: ["start 75%", "end 55%"]
  });
  return <>
      <PageHero variant="full" eyebrow="UNIVERSITY PATHWAYS" title="Where will MCC take you?" description="A clear route from a classroom in Lekki to universities around the world." image={IMAGES.portrait} imageAlt="MCC student in a library" breadcrumb="University Pathways" data-id="element-1436" data-mp="3uirim7o6owpw" />

      <main data-id="element-1437" data-mp="3gizz5reweo4i">
        <section className="mx-auto grid max-w-[1440px] gap-16 px-5 py-28 md:px-9 lg:grid-cols-12 lg:py-40" data-id="element-1438" data-mp="3gjjgb5wq58hh">
          <div className="lg:col-span-4" data-id="element-1439" data-mp="3bnb5g4pk81g4">
            <div className="lg:sticky lg:top-32" data-id="element-1440" data-mp="31x2v90e8u8oe">
              <p className="text-xs font-semibold tracking-[0.18em] text-[#bb1e35]" data-id="element-1441" data-mp="31wb2dxc16p1t">FOR PARENTS NEW TO THE SYSTEM</p>
              <h2 className="mt-6 text-5xl font-medium leading-[0.92] tracking-[-0.07em] md:text-6xl" data-id="element-1442" data-mp="36ucjwdtt6gb6">The OSSD, in three steps.</h2>
            </div>
          </div>
          <div ref={pathRef} className="relative lg:col-span-7 lg:col-start-6" data-id="element-1443" data-mp="2wz1dcisk0fei">
            <div className="absolute bottom-0 left-[15px] top-0 w-px bg-[#dedbd3]" data-id="element-1444" data-mp="3kp543pkn8ko3" />
            <motion.div style={{
            scaleY: scrollYProgress
          }} className="absolute bottom-0 left-[15px] top-0 w-px origin-top bg-[#bb1e35]" data-id="element-1445" data-mp="3pnq8m16xhqic" />
            {STEPS.map(step => <Reveal key={step.node} className="relative pb-24 pl-16 last:pb-0" data-id="element-1446" data-mp="3fxdna6rahtb5">
                <span className="absolute left-0 top-2 grid h-8 w-8 place-items-center bg-[#fbfaf6]" data-id="element-1447" data-mp="3fv0wd43k08ly">
                  <span className="h-3 w-3 rounded-full border-2 border-[#bb1e35] bg-[#fbfaf6]" data-id="element-1448" data-mp="7fqx03r1thhv" />
                </span>
                <p className="text-[clamp(3.5rem,8vw,7rem)] font-medium leading-none tracking-[-0.08em]" data-id="element-1449" data-mp="1wyfsvrp3b5nx">{step.node}</p>
                <h3 className="mt-6 text-2xl font-medium tracking-[-0.04em]" data-id="element-1450" data-mp="1s0e7v183sw9t">{step.title}</h3>
                <p className="mt-3 max-w-md text-base leading-7 text-[#4f5963]" data-id="element-1451" data-mp="26os957pvgco9">{step.copy}</p>
              </Reveal>)}
          </div>
        </section>

        <section className="bg-[#061f38] py-24 text-white md:py-32" data-id="element-1452" data-mp="21rs4n18o7hz9">
          <div className="mx-auto max-w-[1440px] px-5 md:px-9" data-id="element-1453" data-mp="1e0d5fhc6xgs8">
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end" data-id="element-1454" data-mp="191jthtcjxxu6">
              <div data-id="element-1455" data-mp="1n11ctt72wiw5">
                <p className="text-xs font-semibold tracking-[0.18em] text-[#f0b1b9]" data-id="element-1456" data-mp="1n1kwpfgahhct">DESTINATIONS</p>
                <h2 className="mt-5 text-4xl font-medium tracking-[-0.06em] md:text-6xl" data-id="element-1457" data-mp="1iy4yieokn8y0">One diploma. Many doors.</h2>
              </div>
              <p className="text-xs text-white/50" data-id="element-1458" data-mp="2yhrinb7lb8p8">Swipe or scroll →</p>
            </div>
          </div>
          <div className="mt-14 flex snap-x snap-mandatory gap-px overflow-x-auto bg-white/15 pl-5 [scrollbar-width:none] md:pl-9" data-id="element-1459" data-mp="2ugfrh192ifex">
            {DESTINATIONS.map((d, i) => <article key={d.region} className="flex h-[340px] w-[78vw] max-w-[380px] shrink-0 snap-start flex-col justify-between bg-[#061f38] p-7" data-id="element-1460" data-mp="3tq4z6fdjzaqi" data-mpi={i}>
                <span className="text-xs text-[#f0b1b9]" data-id="element-1461" data-mp="7j6ecl4wsiss">0{i + 1}</span>
                <div data-id="element-1462" data-mp="7gahz18pm26g">
                  <h3 className="text-4xl font-medium leading-none tracking-[-0.06em]" data-id="element-1463" data-mp="ci9j04gz8n08">{d.region}</h3>
                  <p className="mt-4 max-w-[240px] text-sm leading-6 text-white/60" data-id="element-1464" data-mp="gite72hzpgjq">{d.note}</p>
                </div>
              </article>)}
          </div>
        </section>

        <section className="mx-auto max-w-[1440px] px-5 py-28 md:px-9 md:py-36" data-id="element-1465" data-mp="q8v665dgrj3x">
          <p className="text-xs font-semibold tracking-[0.18em] text-[#bb1e35]" data-id="element-1466" data-mp="qaz5sb4bxvye">PUBLISHED ADMISSION OFFERS</p>
          <WordReveal text="MCC students have received offers from top universities worldwide, including:" className="mt-6 max-w-3xl text-3xl font-medium leading-tight tracking-[-0.045em] md:text-4xl" data-id="element-1467" data-mp="107c0ncbm2fuq" />
          <ul className="mt-12 border-t border-[#061f38]" data-id="element-1468" data-mp="2fqjmk6ndb3z0">
            {PUBLISHED_OFFERS.map((name, i) => <li key={name} className="border-b border-[#dedbd3] py-6" data-id="element-1469" data-mp="2aqnh02z0v4hc" data-mpi={i}>
                <Reveal delay={i * 0.08} className="text-[clamp(2rem,5vw,4.5rem)] font-medium leading-none tracking-[-0.06em]" data-id="element-1470" data-mp="2at06r2b0uj4y">{name}</Reveal>
              </li>)}
          </ul>
          <p className="mt-6 text-xs text-[#6b7378]" data-id="element-1471" data-mp="33hxcy71ykphc">As stated on maplecc.ca. Year, programme and individual details to be confirmed by MCC before publication.</p>
        </section>
      </main>

      <CTABand title="Talk through your child’s future route." image={IMAGES.portrait} data-id="element-1472" data-mp="2tnyg9nknlxws" />
    </>;
}